import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Link2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  TrendingUp,
  Users,
} from 'lucide-react';
import { fetchDashboard, fetchTrends, fetchByProvider } from '../api/dashboard';
import { fetchBacklinks } from '../api/backlinks';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { StatCard } from '../components/ui/bento-grid';
import { BackgroundBeams } from '../components/ui/background-beams';
import { NeedsAttention } from '../components/NeedsAttention';
import { AnimatedCard, CardHeader, CardTitle, CardContent } from '../components/ui/AnimatedCard';
import { PageHeader } from '../components/ui/PageHeader';
import { cn } from '../lib/utils';

function Dashboard() {
  const navigate = useNavigate();
  const [scope, setScope] = useState('all'); // 'all' | 'provider'
  const [selectedProviderId, setSelectedProviderId] = useState(null);

  const { data: dashboardData, isLoading } = useQuery({
    queryKey: ['dashboard', selectedProviderId],
    queryFn: () => fetchDashboard(selectedProviderId ? { provider_id: selectedProviderId } : {}),
  });

  const { data: trendsData } = useQuery({
    queryKey: ['dashboard-trends'],
    queryFn: fetchTrends,
  });

  // Fetch all backlinks to show in NeedsAttention
  const { data: backlinksData } = useQuery({
    queryKey: ['all-backlinks'],
    queryFn: () => fetchBacklinks({ limit: 1000, skip_history: true }),
  });

  // Fetch provider stats when in provider scope
  const { data: providerData } = useQuery({
    queryKey: ['dashboard-by-provider'],
    queryFn: fetchByProvider,
    enabled: scope === 'provider',
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-muted rounded mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-muted rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {
    total_backlinks: 0,
    live: 0,
    lost: 0,
    changed: 0,
  };

  const recentAlerts = dashboardData?.recent_alerts || [];
  const trends = trendsData?.trends || [];
  const allBacklinks = backlinksData?.backlinks || [];
  const providerStats = providerData?.providers || [];
  const wow = stats.week_over_week || { live_delta: 0, lost_delta: 0, changed_delta: 0 };

  // Calculate trends (comparing to 7 days ago if we have data)
  const livePercent = stats.total_backlinks > 0
    ? Math.round((stats.live / stats.total_backlinks) * 100)
    : 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <PageHeader
        title="Dashboard"
        description={selectedProviderId ? `Filtered by provider` : 'Monitor your backlinks at a glance'}
      />

      {/* Scope Tabs */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => { setScope('all'); setSelectedProviderId(null); }}
          className={cn(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            scope === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
          )}
        >
          All Projects
        </button>
        <button
          onClick={() => setScope('provider')}
          className={cn(
            'px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5',
            scope === 'provider' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
          )}
        >
          <Users className="h-4 w-4" />
          By Provider
        </button>
        {selectedProviderId && (
          <button
            onClick={() => setSelectedProviderId(null)}
            className="px-3 py-1.5 rounded-lg text-xs bg-warning/20 text-warning hover:bg-warning/30 transition-colors"
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Backlinks"
          value={stats.total_backlinks.toLocaleString()}
          icon={<Link2 className="h-5 w-5" />}
          color="blue"
        />
        <StatCard
          title="Live Links"
          value={stats.live.toLocaleString()}
          description={`${livePercent}% of total`}
          icon={<CheckCircle className="h-5 w-5" />}
          color="green"
          trend={wow.live_delta !== 0 ? wow.live_delta : undefined}
          trendUp={wow.live_delta >= 0}
          trendLabel="vs last week"
        />
        <StatCard
          title="Lost This Week"
          value={stats.lost.toLocaleString()}
          icon={<XCircle className="h-5 w-5" />}
          color="red"
          trend={wow.lost_delta !== 0 ? Math.abs(wow.lost_delta) : (stats.lost > 0 ? stats.lost : undefined)}
          trendUp={wow.lost_delta < 0}
          trendLabel="vs last week"
          onClick={() => navigate('/alerts?type=link_removed,page_404')}
        />
        <StatCard
          title="Status Changed"
          value={stats.changed.toLocaleString()}
          icon={<AlertTriangle className="h-5 w-5" />}
          color="amber"
          trend={wow.changed_delta !== 0 ? Math.abs(wow.changed_delta) : undefined}
          trendUp={wow.changed_delta < 0}
          trendLabel="vs last week"
          onClick={() => navigate('/alerts?type=nofollow_added,redirect')}
        />
      </div>

      {/* Provider Comparison Grid */}
      {scope === 'provider' && providerStats.length > 0 && (
        <AnimatedCard>
          <CardHeader>
            <CardTitle>Provider Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-2 px-3 text-muted-foreground font-medium">Provider</th>
                    <th className="text-right py-2 px-3 text-muted-foreground font-medium">Total</th>
                    <th className="text-right py-2 px-3 text-muted-foreground font-medium">Live %</th>
                    <th className="text-right py-2 px-3 text-muted-foreground font-medium">Lost</th>
                    <th className="text-right py-2 px-3 text-muted-foreground font-medium">Quality</th>
                    <th className="text-right py-2 px-3 text-muted-foreground font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {providerStats.map((p) => (
                    <tr
                      key={p.provider_id || 'unassigned'}
                      className={cn(
                        'border-b border-border/50 hover:bg-muted/50 transition-colors cursor-pointer',
                        selectedProviderId === p.provider_id && 'bg-primary/10'
                      )}
                      onClick={() => {
                        if (p.provider_id) {
                          setSelectedProviderId(selectedProviderId === p.provider_id ? null : p.provider_id);
                        }
                      }}
                    >
                      <td className="py-2.5 px-3 font-medium text-foreground">{p.provider_name}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{p.total}</td>
                      <td className="py-2.5 px-3 text-right">
                        <span className={cn(
                          'tabular-nums font-medium',
                          p.live_pct >= 90 ? 'text-success' : p.live_pct >= 70 ? 'text-warning' : 'text-destructive'
                        )}>
                          {p.live_pct}%
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        {p.lost > 0 ? <span className="text-destructive">{p.lost}</span> : <span className="text-muted-foreground">0</span>}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        {p.avg_quality != null ? (
                          <span className={cn(
                            'font-medium',
                            p.avg_quality >= 60 ? 'text-success' : p.avg_quality >= 35 ? 'text-warning' : 'text-destructive'
                          )}>
                            {p.avg_quality}
                          </span>
                        ) : <span className="text-muted-foreground">-</span>}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        {p.contact_email && (
                          <a
                            href={`mailto:${p.contact_email}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-primary hover:underline"
                          >
                            Contact
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </AnimatedCard>
      )}

      {/* Needs Attention Section */}
      <NeedsAttention
        backlinks={allBacklinks}
        alerts={recentAlerts}
      />

      {/* Trends Chart */}
      <AnimatedCard className="relative overflow-hidden">
        <BackgroundBeams className="opacity-30" />
        <div className="relative z-10">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle>30-Day Trends</CardTitle>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-0.5 bg-success rounded" />
                <span className="text-muted-foreground">Live</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-0.5 bg-destructive rounded" />
                <span className="text-muted-foreground">Lost</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-0.5 bg-warning rounded" />
                <span className="text-muted-foreground">Changed</span>
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {trends.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trends}>
                    <XAxis
                      dataKey="date"
                      stroke="hsl(var(--muted-foreground))"
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                      labelStyle={{ color: 'hsl(var(--foreground))' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="live_count"
                      stroke="hsl(var(--success))"
                      strokeWidth={2}
                      dot={false}
                      name="Live"
                    />
                    <Line
                      type="monotone"
                      dataKey="lost_count"
                      stroke="hsl(var(--destructive))"
                      strokeWidth={2}
                      dot={false}
                      name="Lost"
                    />
                    <Line
                      type="monotone"
                      dataKey="changed_count"
                      stroke="hsl(var(--warning))"
                      strokeWidth={2}
                      dot={false}
                      name="Changed"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-muted-foreground">
                <TrendingUp className="h-12 w-12 mb-3 opacity-30" />
                <p className="text-sm">No trend data available yet</p>
                <p className="text-xs mt-1">Check some backlinks to start seeing trends</p>
              </div>
            )}
          </CardContent>
        </div>
      </AnimatedCard>
    </div>
  );
}

export default Dashboard;
